import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { useUser } from "@/hooks/useUser";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { FaArrowLeft } from "react-icons/fa6";
import { useMarketplaceStore } from "@/lib/stores/useMarketplaceStore";
import { toast } from "../ui/use-toast";
import { Wallets } from "../profile/Wallets";
import { Checkbox } from "@radix-ui/react-checkbox";
import { Separator } from "@radix-ui/react-menubar";
import { db } from "@/lib/firebase";
import { doc, updateDoc } from "firebase/firestore";

export function Profile() {
  const { user } = useAuth();
  const { user: userData } = useUser(user?.uid);
  const { purchasedProducts } = useMarketplaceStore();
  console.log({ purchasedProducts });

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied to clipboard",
      description: text,
    });
  }

  const handleSubscribe = async (checked: boolean) => {
    if (!user) {
      return;
    }
    try {
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        enableNotifications: checked,
      });
      toast({
        title: "Success",
        description: checked
          ? "Subscribed to notifications"
          : "Unsubscribed from notifications",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update wallet addresses",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <header className="border-b">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/" className="flex items-center gap-2">
                <FaArrowLeft className="h-4 w-4" />
                Back to Dashboard
              </Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 container mx-auto p-6">
        <div className="grid grid-cols-3 gap-6 max-w-6xl mx-auto">
          <div className="col-span-1">
            <Card>
              <CardHeader className="flex flex-row items-center gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={user?.photoURL || ""} />
                  <AvatarFallback>
                    {user?.displayName?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <CardTitle className="text-2xl">
                    {user?.displayName}
                  </CardTitle>
                  <div className="text-xs text-muted-foreground">
                    {user?.email}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {userData?.uid}
                    <i
                      className="fa-regular fa-copy ml-1 cursor-pointer"
                      onClick={() => copyToClipboard(user?.uid || "")}
                    />
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <Badge variant="secondary" className="mt-2">
                      {userData?.role}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-medium mb-2">Account Details</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="text-muted-foreground">Member since</div>
                      <div>
                        {new Date(
                          userData?.registeredAt || 0,
                        ).toLocaleDateString()}
                      </div>
                      <div className="text-muted-foreground">Last login</div>
                      <div>
                        {new Date(
                          userData?.lastLoggedIn || 0,
                        ).toLocaleDateString()}
                      </div>
                      <div className="text-muted-foreground">Referral</div>
                      <div>{userData?.referral}</div>
                    </div>
                  </div>
                </div>
                <Separator />
                <Checkbox
                  className="text-xxs mt-4"
                  id="enableNotifications"
                  checked={userData?.enableNotifications}
                  onCheckedChange={(checked) =>
                    handleSubscribe(Boolean(checked))
                  }
                >
                  <i
                    className={`fa-regular fa-${userData?.enableNotifications ? "bell" : "bell-slash"} mr-2`}
                  />
                  Subscribe to ticket updates and news from Intercom
                </Checkbox>
              </CardContent>
            </Card>
          </div>

          <div className="col-span-2">
            <div className="grid gap-4">
              {userData && <Wallets user={userData} />}
              <Card>
                <CardHeader>
                  <CardTitle className="text-xl">Purchased Products</CardTitle>
                </CardHeader>
                <CardContent>
                  {purchasedProducts.length > 0 ? (
                    <div className="relative w-full overflow-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 font-medium">Name</th>
                            <th className="text-right py-2 font-medium w-[50px]">
                              Variations
                            </th>
                            <th className="text-right py-2 font-medium">
                              Paid with
                            </th>
                            <th className="text-right py-2 font-medium">
                              Paid until
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {purchasedProducts.map((product) => {
                            const purchasedProduct = purchasedProducts?.find(
                              (p) => p.id === product.id,
                            );

                            return (
                              <tr key={product.id} className="border-b">
                                <td className="py-2">{product.name}</td>
                                <td className="py-2 text-right space-x-1">
                                  <div className="flex justify-end gap-1 flex-wrap">
                                    {product.variants.length > 0 &&
                                      product.variants.map((variation) => (
                                        <Badge
                                          key={variation}
                                          variant="secondary"
                                        >
                                          {variation}
                                        </Badge>
                                      ))}
                                  </div>
                                </td>
                                <td className="py-2 text-right">
                                  <Badge
                                    key={product.paymentMethod}
                                    variant="secondary"
                                  >
                                    {product.paymentMethod}
                                  </Badge>
                                </td>
                                <td className="py-2 text-right w-[180px]">
                                  {purchasedProduct?.paidUntil ? (
                                    <span className="text-muted-foreground">
                                      {new Date(
                                        purchasedProduct.paidUntil,
                                      ).toLocaleDateString()}
                                    </span>
                                  ) : (
                                    <span className="text-muted-foreground">
                                      -
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-muted-foreground text-sm">
                      No purchased products
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
